import { createBrowserRouter, RouterProvider, useRouteError } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PostHogProvider } from 'posthog-js/react';

import { AnalyticsProvider } from '@/analytics';
import { ConfirmHost } from '@/components/molecules/ConfirmDialog';

import './i18n';

import { Toaster } from './components/molecules/ToastTemplate/Toaster';
import SentryErrorBoundary from './components/SentryErrorBoundary';
import { getPostHogConfig } from './config/posthog';
import { AuthProvider, SessionExpiryWatcher } from './contexts/auth';
import { DemoProvider } from './contexts/demo/DemoContext';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext';
import MqttProvider from './contexts/mqtt-provider-context';
import { ThemeProvider } from './contexts/theme';
import { retryUnlessRefused } from './domain/shared/retry';
import Routes from './routes';
import { isEphemeralHostname } from './utils/environmentByHostname';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            staleTime: 1000 * 60 * 5,
            gcTime: 1000 * 60 * 10,
            retry: retryUnlessRefused,
        },
    },
});

const posthogConfig = getPostHogConfig();
// Per-PR ephemeral environments (uat-pr-*/preview-pr-*) must send no
// analytics data to PostHog, but SHOULD still read feature flags so UAT
// behaves like production. opt_out_capturing_by_default drops every event
// locally (sessions, pageviews, $feature_flag_called, identify) while the
// flags endpoint stays enabled; surveys and external script loading are
// disabled so the flags request is the only PostHog traffic.
//
// Known limitation: because identify() is dropped, flags are evaluated
// against an anonymous distinct_id — boolean and percentage-rollout flags
// work, but flags targeting user properties/cohorts will not match in
// ephemeral environments.
const isEphemeral = isEphemeralHostname();

/** Everything under the router: these providers read the location, so they render inside it. */
function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <AuthProvider>
                <SessionExpiryWatcher />
                <AnalyticsProvider>
                    <FeatureFlagsProvider>
                        <ThemeProvider>
                            <DemoProvider>
                                <MqttProvider>
                                    <Toaster />
                                    <ConfirmHost />
                                    <Routes />
                                </MqttProvider>
                            </DemoProvider>
                        </ThemeProvider>
                    </FeatureFlagsProvider>
                </AnalyticsProvider>
            </AuthProvider>
        </QueryClientProvider>
    );
}

/**
 * A data router catches a render error in its own boundary; thrown again from there, it
 * reaches SentryErrorBoundary as it did under BrowserRouter.
 */
function RethrowRouteError(): never {
    throw useRouteError();
}

// One splat route: the pages stay the declarative tree in routes/, and the data router is what
// lets a page hold the reader back while it has unsaved changes (useBlocker).
const router = createBrowserRouter([
    { path: '*', element: <App />, errorElement: <RethrowRouteError /> },
]);

export default function Root() {
    return (
        <SentryErrorBoundary>
            <PostHogProvider
                apiKey={posthogConfig.apiKey}
                options={{
                    api_host: posthogConfig.apiHost,
                    autocapture: false,
                    capture_pageview: false,
                    capture_pageleave: false,
                    disable_session_recording: true,
                    ...(isEphemeral && {
                        opt_out_capturing_by_default: true,
                        disable_surveys: true,
                        disable_external_dependency_loading: true,
                    }),
                }}>
                {/* Navigations render synchronously, as they did on react-router 5: with transitions
                    a lazy page would keep the previous page on screen instead of its spinner. */}
                <RouterProvider router={router} useTransitions={false} />
            </PostHogProvider>
        </SentryErrorBoundary>
    );
}
